/**
 * Google Consent Mode v2 defaults + updates.
 *
 * Pure, side-effect-free builders. The GTM component serializes the output of
 * `buildConsentDefaultsByDecision()` into the inline bootstrap script so that
 * `gtag('consent','default', …)` is guaranteed to run BEFORE `gtm.js` is
 * inserted — Consent Mode only honors a default that precedes the container.
 *
 * Region policy (Phase 1 GTM):
 *   - EEA + UK  → every ad/analytics storage signal starts DENIED.
 *   - Elsewhere → starts GRANTED (the banner can still revoke it).
 *   - A stored decision from an earlier visit wins EVERYWHERE and drops the
 *     region split entirely — the user already answered.
 *
 * `functionality_storage` / `security_storage` are always granted: they cover
 * session, load-balancing and fraud-prevention storage, which is strictly
 * necessary and outside the consent gate.
 */

import type { AnalyticsConsent } from '../consent/analytics-consent';

export type ConsentSignal = 'granted' | 'denied';

/**
 * ISO 3166-1 alpha-2 codes of the 30 EEA member states (EU-27 + Iceland,
 * Liechtenstein, Norway). Google matches `region` against these codes (and
 * `COUNTRY-SUBDIVISION` forms, which we don't need).
 */
export const EEA_COUNTRY_CODES = [
	'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
	'DE', 'GR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV',
	'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'ES',
] as const;

/**
 * The regions that get a DENIED default. EEA plus the UK — post-Brexit the UK
 * is out of the EEA but PECR/UK-GDPR impose the same prior-consent rule, and
 * Google's own consent-mode guidance groups them.
 */
export const CONSENT_DENIED_REGIONS: readonly string[] = [...EEA_COUNTRY_CODES, 'GB'];

/** How long GTM holds tags back waiting for a `consent update`, in ms. */
export const CONSENT_WAIT_FOR_UPDATE_MS = 500;

/** The four signals the banner decision drives. */
export interface ConsentSignals {
	ad_storage: ConsentSignal;
	ad_user_data: ConsentSignal;
	ad_personalization: ConsentSignal;
	analytics_storage: ConsentSignal;
}

export interface ConsentDefaultCommand extends ConsentSignals {
	/** Always granted — strictly-necessary storage, never gated. */
	functionality_storage: ConsentSignal;
	security_storage: ConsentSignal;
	wait_for_update: number;
	/** Absent = the catch-all default; present = region-scoped override. */
	region?: readonly string[];
}

/** Which persisted banner decision a set of defaults is built for. */
export type ConsentDecisionKey = 'undecided' | 'granted' | 'denied';

const signals = (state: ConsentSignal): ConsentSignals => ({
	ad_storage: state,
	ad_user_data: state,
	ad_personalization: state,
	analytics_storage: state,
});

const defaultCommand = (
	state: ConsentSignal,
	region?: readonly string[],
): ConsentDefaultCommand => ({
	...signals(state),
	functionality_storage: 'granted',
	security_storage: 'granted',
	wait_for_update: CONSENT_WAIT_FOR_UPDATE_MS,
	...(region ? { region } : {}),
});

/**
 * Defaults for a visitor who has NOT decided yet.
 *
 * Two commands, catch-all first: Google applies the most specific matching
 * `region` regardless of order, but emitting the broad grant first and the
 * narrow denial second keeps the intent readable in Tag Assistant's log.
 */
export const buildUndecidedConsentDefaults = (): ConsentDefaultCommand[] => [
	defaultCommand('granted'),
	defaultCommand('denied', CONSENT_DENIED_REGIONS),
];

/**
 * Defaults for every persisted decision state, keyed so the inline bootstrap
 * can pick one at runtime after reading localStorage (the decision is not
 * known at render time — the script is identical for every visitor and cached).
 */
export const buildConsentDefaultsByDecision = (): Record<
	ConsentDecisionKey,
	ConsentDefaultCommand[]
> => ({
	undecided: buildUndecidedConsentDefaults(),
	// An explicit answer overrides geography in BOTH directions: a granted
	// visitor in Germany must not be re-denied by the region rule, and a denied
	// visitor in the US must not be granted by the catch-all.
	granted: [defaultCommand('granted')],
	denied: [defaultCommand('denied')],
});

/**
 * Payload for `gtag('consent','update', …)`, fired the moment the banner is
 * answered. Only the four gated signals — the two always-granted ones never
 * change, and re-sending them adds noise to the Tag Assistant consent log.
 */
export const buildConsentUpdate = (consent: AnalyticsConsent): ConsentSignals =>
	signals(consent === 'granted' ? 'granted' : 'denied');
